/**
 * Memory API handlers — typed fetch wrappers for the backend memories API.
 */

import { handleUnauthorized } from '@/lib/api-error-handler'
import { BACKEND_URL } from '@/lib/config'
import { getAuthToken } from '@/lib/supabase'

export type MemoryType = 'fact' | 'preference' | 'context' | 'instruction'
export type MemoryScope = 'global' | 'workbook'

export type Memory = {
  id: string
  content: string
  type: MemoryType
  scope: MemoryScope
  workbook_key: string | null
  access_count: number
  last_accessed_at: string | null
  created_at: string
  updated_at: string
}

async function memoryFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getAuthToken()
  if (!token) throw new Error('Not authenticated')
  if (!BACKEND_URL) throw new Error('Backend URL not configured')

  const response = await fetch(`${BACKEND_URL}/api/memories${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
      ...(options.headers || {}),
    },
  })

  if (response.status === 401) {
    await handleUnauthorized(response, 'Memory API')
    throw new Error('Unauthorized')
  }

  return response
}

export async function getRelevantMemories(
  workbookKey: string | null,
  limit = 15
): Promise<Memory[]> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (workbookKey) params.set('workbook_key', workbookKey)

  const response = await memoryFetch(`/relevant?${params.toString()}`)

  if (!response.ok) {
    throw new Error(`Failed to fetch relevant memories: ${response.status}`)
  }

  return response.json()
}

export async function searchMemories(
  query: string,
  scope?: MemoryScope | 'both',
  workbookKey?: string,
  limit = 20
): Promise<Memory[]> {
  const params = new URLSearchParams({ query, limit: String(limit) })
  if (scope && scope !== 'both') params.set('scope', scope)
  if (workbookKey) params.set('workbook_key', workbookKey)

  const response = await memoryFetch(`/search?${params.toString()}`)

  if (!response.ok) {
    throw new Error(`Failed to search memories: ${response.status}`)
  }

  return response.json()
}

export async function listMemories(
  scope?: MemoryScope | 'both',
  workbookKey?: string
): Promise<Memory[]> {
  const params = new URLSearchParams()
  if (scope && scope !== 'both') params.set('scope', scope)
  if (workbookKey) params.set('workbook_key', workbookKey)

  const qs = params.toString()
  const response = await memoryFetch(qs ? `?${qs}` : '')

  if (!response.ok) {
    throw new Error(`Failed to list memories: ${response.status}`)
  }

  return response.json()
}

export async function updateMemory(
  id: string,
  params: { content: string; type?: MemoryType }
): Promise<Memory> {
  const response = await memoryFetch(`/${id}`, {
    method: 'PUT',
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(
      data.detail || `Failed to update memory: ${response.status}`
    )
  }

  return response.json()
}

export async function deleteMemory(id: string): Promise<void> {
  const response = await memoryFetch(`/${id}`, { method: 'DELETE' })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(
      data.detail || `Failed to delete memory: ${response.status}`
    )
  }
}
